"use client";

import * as React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, FileCheck2, Clock, CircleDollarSign, HeartHandshake } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
const faqItems = [{
  id: "item-1",
  icon: ShieldCheck,
  question: "What is final expense insurance and what costs does it cover?",
  summary: "A specialized, permanent life insurance policy built for end-of-life expenses.",
  answer: "Final expense insurance is generally a form of permanent life insurance designed to provide a smaller death benefit that can help beneficiaries with expenses after the insured person's death. Beneficiaries may use the proceeds for funeral or burial expenses, cremation, medical bills, outstanding debts, or other financial needs. Unless the policy or another arrangement restricts how the benefit is used, the beneficiary generally decides how to use the proceeds. Life insurance proceeds received by a beneficiary because of the insured person's death are generally excluded from federal gross income. Exceptions can apply, and interest paid on proceeds may be taxable. Learn more / Verify: IRS — Life Insurance & Disability Insurance Proceeds.",
  caveat: "Benefit amounts typically range between $5,000 and $25,000, ensuring premiums remain affordable while adequately covering actual burial and ceremony expenses."
}, {
  id: "item-2",
  icon: FileCheck2,
  question: "How does final expense insurance differ from traditional term or whole life coverage?",
  summary: "Smaller face values, simplified qualification, and guaranteed permanent protection.",
  answer: "Final expense insurance is typically a form of whole life insurance with a smaller death benefit and is often designed to help cover end-of-life expenses and other financial needs. Term life insurance provides coverage for a specified period. Traditional whole life and final expense whole life are forms of permanent insurance, but available coverage amounts, underwriting requirements, premiums, benefits, and other policy features can differ by product and insurance carrier. A final expense policy can remain in force for life when the policy requirements are met. Always review the specific contract for its premiums, guarantees, exclusions, and other provisions.",
  caveat: ""
}, {
  id: "item-3",
  icon: HeartHandshake,
  question: "Are medical exams required to qualify for final expense policies?",
  summary: "Most applicants qualify through a simple health questionnaire without physicals or blood draws.",
  answer: "Many final expense policies use simplified underwriting and may not require a traditional medical exam. Instead, the application may include health questions and other underwriting information. Some products may offer guaranteed-issue coverage without health questions. Guaranteed-issue and other policies may include a graded or reduced death benefit during an initial period for certain causes of death.",
  caveat: ""
}, {
  id: "item-4",
  icon: Clock,
  question: "How quickly do beneficiaries receive death benefit payouts?",
  summary: "Claims are generally settled in days once basic documentation is submitted.",
  answer: "Claim-processing time varies by insurance company and the circumstances of the claim. The beneficiary generally must notify the insurance company and provide the documentation required by the carrier, which may include a claim form and certified death certificate. A claim may take longer when additional information or review is required. Because processing times vary, DJTD Consulting does not promise a specific number of days for payment.",
  caveat: "When life insurance proceeds are payable directly to a properly designated beneficiary, they generally do not become part of the probate estate. Different rules can apply when the estate is the beneficiary or depending on the beneficiary arrangement and applicable law."
}, {
  id: "item-5",
  icon: CircleDollarSign,
  question: "Can coverage amounts be adjusted or used for non-funeral debts?",
  summary: "Beneficiaries have complete freedom over how the cash payout is allocated.",
  answer: "Final expense life insurance provides a death benefit to the policy's beneficiary. Unless the policy, beneficiary arrangement, or other circumstances restrict how the proceeds are used, the beneficiary generally decides how to use the money. It may be used for funeral or burial costs, medical bills, debts, household expenses, or other needs. Coverage amounts, policy changes, and available options depend on the specific policy and insurance carrier. Increasing coverage may require a new application or additional underwriting.",
  caveat: "Policyowners may also be able to name contingent beneficiaries who can receive the death benefit if the primary beneficiary cannot receive it."
}];
export default function FinalExpense() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        duration: shouldReduceMotion ? 0 : 0.35
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="final-expense" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mx-auto max-w-3xl text-center mb-14 md:mb-18">
          <div className="inline-flex items-center justify-center mb-4">
            <Badge variant="outline" className="bg-accent/40 text-foreground border-border px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-lg">
              Final Expense & Burial Coverage
            </Badge>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl md:text-4xl lg:text-[2.65rem] font-bold text-foreground tracking-tight leading-[1.2] mb-4">
            Clear answers on final expense insurance
          </h2>

          {/* Architectural Anchor Dot Rule */}
          <div className="flex items-center justify-center gap-1.5 my-5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="h-[1px] w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="h-[1px] w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="h-[1px] w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          </div>

          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Understand how final expense insurance may help with burial costs, outstanding bills, and other financial needs after death, while providing a death benefit to designated beneficiaries.
          </p>
        </motion.div>

        {/* Q&A Single-Column Accordion */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="mx-auto max-w-[840px] mb-12">
          <Accordion type="single" collapsible className="w-full space-y-3.5">
            {faqItems.map((faq, index) => {
            const IconComponent = faq.icon;
            return <motion.div key={faq.id} variants={itemVariants} data-index={index}>
                  <AccordionItem value={faq.id} className="border border-border bg-card rounded-xl shadow-xs overflow-hidden transition-[border-color,box-shadow] hover:border-chart-2/40 hover:shadow-sm">
                    <AccordionTrigger className="px-5 sm:px-6 py-4 sm:py-5 hover:no-underline text-left font-default text-base sm:text-lg font-semibold text-foreground [&[data-state=open]]:border-b [&[data-state=open]]:border-border/60">
                      <div className="flex items-center gap-3.5 sm:gap-4 pr-3">
                        <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                          <IconComponent className="size-4 sm:size-5" />
                        </div>
                        <span className="leading-snug">{faq.question}</span>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-5 sm:px-6 pt-4 sm:pt-5 pb-5 sm:pb-6 text-foreground">
                      <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                        {faq.answer}
                      </p>

                      {/* Clarity Guidance Plinth / Policy Caveat Box */}
                      {faq.caveat && <div className="rounded-lg border border-border/80 bg-muted/50 p-3.5 sm:p-4 text-xs sm:text-sm text-foreground">
                          <div className="flex items-start gap-2.5">
                            <HelpCircle className="size-4 text-chart-2 shrink-0 mt-0.5" />
                            <div className="leading-relaxed">
                              <span className="font-semibold text-foreground">Advisory note: </span>
                              <span className="text-muted-foreground">{faq.caveat}</span>
                            </div>
                          </div>
                        </div>}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>;
          })}
          </Accordion>
        </motion.div>

        {/* Advisory Guidance Plinth Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mx-auto max-w-[840px]">
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs flex items-start sm:items-center gap-4 sm:gap-5">
            <div className="flex size-11 sm:size-12 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
              <PhoneCall className="size-5" />
            </div>
            <div>
              <h3 className="font-default text-lg font-semibold text-foreground mb-1">
                Want to compare final expense options?
              </h3>
              <p className="font-default text-sm text-muted-foreground leading-relaxed">
                Connect with DJTD Consulting to review available final expense options from multiple insurance carriers.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}